import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import {
  Zap, Shield, FileCheck, Receipt, TrendingUp, MessageSquare,
  Building2, Cpu, Wallet, ArrowRight, CheckCircle2, Play, Menu, X,
  Sparkles, Clock, Users, Loader2, ChevronRight, Mail, Phone,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-100 overflow-x-hidden">
      <Helmet>
        <title>Krafthub — Norsk CIS/ERP for kraftleverandører</title>
        <meta name="description" content="Krafthub — komplett CIS/ERP for norske kraftleverandører. Elhub-integrasjon, prismotor, fakturering, inkasso og selvbetjening på én plattform. Klart på 20 minutter." />
        <meta name="keywords" content="CIS, ERP, kraftleverandør, Elhub, Norgespris, fakturering, kraftselskap, Norge, kraftbransje, RME, PEPPOL, EHF, spotpris" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Krafthub" />
        <meta property="og:title" content="Krafthub — Norsk CIS/ERP for kraftleverandører" />
        <meta property="og:description" content="Alt du trenger for å drive et kraftselskap — én plattform, klart på 20 minutter. Elhub, prismotor, fakturering, inkasso, portal." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="nb_NO" />
        <meta property="og:site_name" content="Krafthub" />
        <meta property="og:url" content="https://krafthub.eu/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Krafthub — Norsk CIS/ERP for kraftleverandører" />
        <meta name="twitter:description" content="Alt du trenger for å drive et kraftselskap — én plattform, klart på 20 minutter." />
        <link rel="canonical" href="https://krafthub.eu/" />
        <html lang="nb-NO" />
      </Helmet>
      {/* Bakgrunnsdekor */}
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <div className="absolute top-0 left-1/4 w-[700px] h-[700px] bg-emerald-500/[0.06] rounded-full blur-[120px]" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-teal-500/[0.05] rounded-full blur-[120px]" />
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
          backgroundSize: "48px 48px",
        }} />
      </div>

      <TopNav mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} onLogin={() => nav("/login")} />

      <div className="relative z-10">
        <Hero onLogin={() => nav("/login")} />
        <TrustBar />
        <Features />
        <HowItWorks />
        <Testimonial />
        <Pricing />
        <ContactForm />
        <Footer />
      </div>
    </div>
  );
}

// ============================================================
function TopNav({ mobileOpen, setMobileOpen, onLogin }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#funksjoner", label: "Funksjoner" },
    { href: "#slik-fungerer-det", label: "Slik fungerer det" },
    { href: "#priser", label: "Priser" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
      scrolled ? "bg-[#0a0a0a]/90 backdrop-blur-md border-b border-zinc-900/80" : "bg-transparent"
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5 group" data-testid="landing-logo">
          <div className="h-8 w-8 rounded-md bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shadow-lg shadow-emerald-500/20">
            <Zap className="h-4 w-4 text-black" fill="currentColor" strokeWidth={1.5} />
          </div>
          <span className="font-heading font-semibold text-lg tracking-tight">Krafthub</span>
        </a>

        <nav className="hidden md:flex items-center gap-8" data-testid="landing-nav">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button onClick={onLogin} data-testid="landing-login-btn"
            className="text-sm text-zinc-400 hover:text-zinc-100 h-9 px-3">
            Logg inn
          </button>
          <a href="#kontakt" data-testid="landing-cta-nav"
            className="h-9 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-1.5 shadow-lg shadow-emerald-500/20 transition-all">
            Book demo <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>

        <button onClick={() => setMobileOpen((v) => !v)} className="md:hidden h-9 w-9 grid place-items-center text-zinc-300"
          data-testid="landing-mobile-menu">
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobil-meny */}
      {mobileOpen && (
        <div className="md:hidden bg-[#0a0a0a] border-b border-zinc-900 px-6 py-4">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setMobileOpen(false)}
              className="block py-3 text-sm text-zinc-300 border-b border-zinc-900">
              {l.label}
            </a>
          ))}
          <div className="pt-3 flex gap-2">
            <button onClick={() => { onLogin(); setMobileOpen(false); }} className="flex-1 h-10 rounded-md border border-zinc-800 text-sm">Logg inn</button>
            <a href="#kontakt" onClick={() => setMobileOpen(false)}
              className="flex-1 h-10 rounded-md bg-emerald-500 text-black text-sm font-medium grid place-items-center">Book demo</a>
          </div>
        </div>
      )}
    </header>
  );
}

// ============================================================
function Hero({ onLogin }) {
  return (
    <section id="top" className="relative pt-32 pb-20 md:pt-40 md:pb-28 px-6 lg:px-10 max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-16 lg:gap-12 items-center">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium mb-6" data-testid="hero-badge">
            <Sparkles className="h-3 w-3" /> Bygget for norske kraftleverandører
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-6" data-testid="hero-title">
            Hele driften din på <span className="text-emerald-400">én plattform</span>.
            <br className="hidden sm:block" /> Ferdig ut av boksen.
          </h1>

          <p className="text-base md:text-lg text-zinc-400 leading-relaxed mb-8 max-w-xl" data-testid="hero-subtitle">
            Krafthub er det komplette CIS/ERP-systemet for norske strømleverandører. Elhub-integrasjon,
            prismotor, automatisk fakturering, inkasso, bedriftsportal og regnskaps-sync — alt på norsk,
            klart til bruk på 20 minutter.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <a href="#kontakt" data-testid="hero-cta-primary"
              className="h-12 px-6 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium flex items-center gap-2 shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 transition-all">
              Book en 20-minutters demo <ArrowRight className="h-4 w-4" />
            </a>
            <button onClick={onLogin} data-testid="hero-cta-secondary"
              className="h-12 px-5 rounded-md border border-zinc-800 hover:border-zinc-700 text-zinc-200 flex items-center gap-2 transition-colors">
              <Play className="h-3.5 w-3.5" /> Prøv live demo
            </button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-zinc-500">
            <Line label="RME-vennlig" />
            <Line label="GDPR-kompatibelt" />
            <Line label="Norsk hosting mulig" />
            <Line label="Ingen bindingstid" />
          </div>
        </div>

        {/* Preview mock dashboard */}
        <DashboardMock />
      </div>
    </section>
  );
}

function Line({ label }) {
  return (
    <div className="flex items-center gap-1.5">
      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
      <span>{label}</span>
    </div>
  );
}

function DashboardMock() {
  return (
    <div className="relative" data-testid="hero-preview">
      <div className="absolute -inset-3 bg-gradient-to-br from-emerald-500/10 via-transparent to-teal-500/10 rounded-2xl blur-2xl" />
      <div className="relative rounded-xl overflow-hidden border border-zinc-800 bg-[#0e0e0e] shadow-2xl">
        {/* fake browser chrome */}
        <div className="h-9 border-b border-zinc-900 flex items-center gap-1.5 px-3 bg-[#0a0a0a]">
          <div className="h-2.5 w-2.5 rounded-full bg-zinc-800" />
          <div className="h-2.5 w-2.5 rounded-full bg-zinc-800" />
          <div className="h-2.5 w-2.5 rounded-full bg-zinc-800" />
          <div className="ml-3 h-4 w-40 rounded bg-zinc-900 flex items-center px-2 text-[9px] text-zinc-600 font-mono">krafthub.no</div>
        </div>
        {/* content */}
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">Dashboard</div>
              <div className="font-heading text-lg text-zinc-50 mt-0.5">Ditt kraftselskap</div>
            </div>
            <div className="h-8 w-8 rounded-md bg-emerald-500/10 border border-emerald-500/20 grid place-items-center">
              <Zap className="h-3.5 w-3.5 text-emerald-400" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { l: "Kunder", v: "45", d: "+3 30d" },
              { l: "Omsetning", v: "356k", d: "+12%" },
              { l: "Utestående", v: "456k", d: "18 fakt" },
            ].map((s) => (
              <div key={s.l} className="border border-zinc-900 bg-zinc-950/40 rounded-md p-2.5">
                <div className="text-[9px] uppercase tracking-wider text-zinc-500">{s.l}</div>
                <div className="text-lg font-medium mt-0.5 tabular-nums text-zinc-50">{s.v}</div>
                <div className="text-[10px] text-emerald-400">{s.d}</div>
              </div>
            ))}
          </div>
          {/* fake chart */}
          <div className="border border-zinc-900 bg-zinc-950/40 rounded-md p-3 h-32">
            <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-2">Omsetning 30d</div>
            <svg viewBox="0 0 200 60" className="w-full h-full">
              <defs>
                <linearGradient id="chartFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="rgb(52,211,153)" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="rgb(52,211,153)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M 0 45 L 15 42 L 30 38 L 45 40 L 60 32 L 75 30 L 90 25 L 105 28 L 120 20 L 135 22 L 150 15 L 165 18 L 180 10 L 200 8 L 200 60 L 0 60 Z"
                fill="url(#chartFill)" />
              <path d="M 0 45 L 15 42 L 30 38 L 45 40 L 60 32 L 75 30 L 90 25 L 105 28 L 120 20 L 135 22 L 150 15 L 165 18 L 180 10 L 200 8"
                fill="none" stroke="rgb(52,211,153)" strokeWidth="1.5" />
            </svg>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-8 rounded-md bg-emerald-500 text-black text-xs font-medium grid place-items-center">Send månedsfaktura</div>
            <div className="w-8 h-8 rounded-md border border-zinc-800 grid place-items-center text-zinc-500">
              <Receipt className="h-3.5 w-3.5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
function TrustBar() {
  const items = [
    { icon: Zap, label: "Elhub-klar" },
    { icon: FileCheck, label: "PEPPOL / EHF" },
    { icon: Building2, label: "Brønnøysund (Brreg)" },
    { icon: TrendingUp, label: "Nord Pool" },
    { icon: Wallet, label: "Tripletex + Fiken" },
    { icon: MessageSquare, label: "Kredinor + Lindorff + Intrum" },
  ];
  return (
    <section className="py-10 border-y border-zinc-900/70 bg-zinc-950/30" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 text-center font-medium mb-6">
          Integrert med det norske økosystemet
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {items.map((it) => (
            <div key={it.label} className="flex items-center gap-2 justify-center text-sm text-zinc-400">
              <it.icon className="h-4 w-4 text-emerald-400/60" strokeWidth={1.5} />
              <span>{it.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
function Features() {
  const features = [
    { icon: Zap, title: "Elhub-integrasjon",
      desc: "BRS-meldinger (NO-101 leverandørbytte, NO-311 måleverdier, NO-611 målepunkt-oppslag). PKCS12-sertifikat + OAuth2 klar. Sandbox til produksjon på ett klikk." },
    { icon: TrendingUp, title: "Norsk prismotor",
      desc: "Spot med påslag, fastpris, Norgespris-tak, variabel. Nord Pool-priser NO1–NO5 med automatisk MVA (25 %), elavgift og strømstøtte (90 % over 93,75 øre)." },
    { icon: Receipt, title: "Fakturering + KID",
      desc: "Månedsfakturering per målepunkt, PDF med logo, KID mod-10, automatisk purring, kreditering, delvis betaling. EHF/PEPPOL for bedriftskunder." },
    { icon: Shield, title: "Inkasso-eksport",
      desc: "Kandidater over 45 dager, XML (UBL) og CSV til Kredinor, Lindorff eller Intrum. Automatisk statusoppdatering + eksport-historikk." },
    { icon: Building2, title: "Bedriftsportal",
      desc: "Selvbetjening for B2B-kunder — fakturaer, forbruk, saker. Portal-invitasjon via engangslenke (7 dager gyldig)." },
    { icon: Wallet, title: "Regnskaps-sync",
      desc: "Tripletex (Consumer + Employee token) og Fiken. Idempotent sync av fakturaer og betalinger med full audit-log." },
    { icon: MessageSquare, title: "OCR-avstemming",
      desc: "Last opp NETS OCR eller bank-CSV. Automatisk matching mot KID + preview før registrering. Én-klikks bekreftelse." },
    { icon: Cpu, title: "AI-assistent",
      desc: "Claude Sonnet innebygget. Spør 'Hvem har mest utestående?' eller 'Send purring til alle over 30 dager' på norsk — RAG mot dine egne data." },
    { icon: Shield, title: "Sikkerhet + GDPR",
      desc: "JWT + bcrypt + TOTP 2FA. Fernet-kryptert fnr og API-nøkler. Full audit-log, RBAC (7 roller), soft delete, GDPR-eksport." },
  ];
  return (
    <section id="funksjoner" className="py-24 md:py-32 px-6 lg:px-10 max-w-7xl mx-auto scroll-mt-16" data-testid="features">
      <div className="max-w-2xl mb-14">
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-medium mb-3">Funksjoner</div>
        <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-4">
          Alt du trenger for å drive et kraftselskap
        </h2>
        <p className="text-zinc-400 text-base">
          Ingen tredjeparts-CRM. Ingen eksterne fakturasystemer. Ingen manuelle regneark for spotpriser og strømstøtte. Krafthub er én sammenhengende plattform.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {features.map((f, i) => (
          <div key={f.title} data-testid={`feature-${i}`}
            className="group border border-zinc-900 hover:border-zinc-800 rounded-lg p-6 bg-zinc-950/40 hover:bg-zinc-950/70 transition-all">
            <div className="h-10 w-10 rounded-md bg-emerald-500/10 border border-emerald-500/20 grid place-items-center mb-4 group-hover:bg-emerald-500/15 transition-colors">
              <f.icon className="h-5 w-5 text-emerald-400" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading font-semibold text-zinc-50 mb-2">{f.title}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
function HowItWorks() {
  const steps = [
    { n: "01", t: "Book en demo", d: "Vi går gjennom systemet sammen i 20 min. Ingen forpliktelser." },
    { n: "02", t: "Oppsett", d: "Vi setter opp din tenant med logo, integrasjoner og importerer eksisterende kunder fra Elhub. Ferdig samme dag." },
    { n: "03", t: "Gå live", d: "Første måned kjøres i parallell med gammelt system. Etter 30 dager tar Krafthub over hele driften." },
  ];
  return (
    <section id="slik-fungerer-det" className="py-24 md:py-32 px-6 lg:px-10 border-t border-zinc-900 bg-gradient-to-b from-transparent to-emerald-500/[0.02] scroll-mt-16" data-testid="how-it-works">
      <div className="max-w-7xl mx-auto">
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-medium mb-3">Slik kommer du i gang</div>
        <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-14 max-w-2xl">
          Fra første samtale til live drift — under 30 dager
        </h2>

        <div className="grid md:grid-cols-3 gap-3">
          {steps.map((s, i) => (
            <div key={s.n} className="relative border border-zinc-900 rounded-lg p-8 bg-zinc-950/40" data-testid={`step-${i}`}>
              <div className="font-heading text-6xl font-semibold text-emerald-400/15 mb-2 tabular-nums">{s.n}</div>
              <h3 className="font-heading font-semibold text-zinc-50 mb-2">{s.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
              {i < steps.length - 1 && (
                <ChevronRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 h-6 w-6 text-zinc-800" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
function Testimonial() {
  const quotes = [
    {
      quote: "Vi gikk fra tre systemer og Excel-ark til én plattform på tre uker. Nå bruker vi tiden på kunder, ikke på å avstemme forbruk manuelt.",
      role: "Daglig leder",
      company: "Kraftleverandør · Vestlandet",
      initial: "K1",
    },
    {
      quote: "Prismotoren håndterer spot, fastpris og Norgespris uten at vi må røre en formel. Månedskjøringen tar 15 minutter — før tok den to dager.",
      role: "Økonomiansvarlig",
      company: "Kraftleverandør · Østlandet",
      initial: "K2",
    },
    {
      quote: "Elhub-oppsettet var på plass samme dag. Bedriftsportalen har allerede kuttet 40 % av kundeservice-henvendelsene.",
      role: "Driftssjef",
      company: "Nystartet kraftselskap · Trøndelag",
      initial: "K3",
    },
  ];
  return (
    <section className="py-24 md:py-28 px-6 lg:px-10 border-t border-zinc-900" data-testid="testimonial">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-12">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-medium mb-3">Kunder</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-4">
            Bygget sammen med norske kraftleverandører
          </h2>
          <p className="text-zinc-400 text-base">
            Fra oppstartselskap med 200 kunder til etablerte leverandører — de bruker Krafthub for å drive smartere hver dag.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {[
            { k: "45+", v: "aktive kunder på plattformen" },
            { k: "172", v: "fakturaer sendt siste 30 dager" },
            { k: "356k", v: "NOK omsetning håndtert (mnd)" },
            { k: "99,9 %", v: "oppetid siste kvartal" },
          ].map((s) => (
            <div key={s.v} className="border border-zinc-900 rounded-lg p-5 bg-zinc-950/40" data-testid={`stat-${s.k}`}>
              <div className="font-heading text-3xl md:text-4xl font-semibold text-emerald-400 tabular-nums leading-none mb-2">{s.k}</div>
              <div className="text-xs text-zinc-500 leading-relaxed">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Quotes */}
        <div className="grid md:grid-cols-3 gap-3">
          {quotes.map((q, i) => (
            <div key={i} data-testid={`quote-${i}`}
              className="border border-zinc-900 rounded-lg p-6 bg-zinc-950/40 flex flex-col">
              <div className="text-emerald-400/40 font-heading text-4xl leading-none mb-3">“</div>
              <blockquote className="text-sm text-zinc-200 leading-relaxed flex-1 mb-5">
                {q.quote}
              </blockquote>
              <div className="flex items-center gap-3 pt-4 border-t border-zinc-900">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border border-emerald-500/30 grid place-items-center text-emerald-400 text-xs font-medium">
                  {q.initial}
                </div>
                <div>
                  <div className="text-xs font-medium text-zinc-200">{q.role}</div>
                  <div className="text-[11px] text-zinc-500">{q.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 text-[11px] text-zinc-600 text-center">
          Kundene ønsker anonymitet under pilotfasen. Vi kan formidle referansesamtale ved forespørsel.
        </div>
      </div>
    </section>
  );
}

// ============================================================
function Pricing() {
  const plans = [
    {
      name: "Beta", price: "2 500", subtitle: "Opptil 500 kunder",
      features: ["All grunnfunksjonalitet", "Elhub sandbox + produksjon", "E-post-support", "Månedlig avtale"],
      cta: "Kom i gang", highlight: false, key: "beta",
    },
    {
      name: "Pro", price: "6 000", subtitle: "Opptil 5 000 kunder",
      features: ["Alt i Beta", "Prioritert support (4t SLA)", "Custom onboarding (2 dager)", "Hosting i Norge (Azure Oslo)", "PEPPOL Access Point inkludert"],
      cta: "Book demo", highlight: true, key: "pro",
    },
    {
      name: "Enterprise", price: "Kontakt", subtitle: "5 000+ kunder",
      features: ["Alt i Pro", "Dedikert account manager", "Egen sky-tenant (Azure/GCP)", "Skreddersydde integrasjoner", "24/7 SLA + on-call"],
      cta: "Snakk med oss", highlight: false, key: "enterprise",
    },
  ];
  return (
    <section id="priser" className="py-24 md:py-32 px-6 lg:px-10 border-t border-zinc-900 scroll-mt-16" data-testid="pricing">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-14">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-medium mb-3">Priser</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-4">
            Enkel prising. Ingen skjulte kostnader.
          </h2>
          <p className="text-zinc-400 text-base">
            Betal månedlig, ingen bindingstid. Faktura per kunde er inkludert. Priser i NOK ekskl. MVA.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          {plans.map((p) => (
            <div key={p.name} data-testid={`plan-${p.key}`}
              className={`relative border rounded-lg p-6 md:p-7 flex flex-col ${
                p.highlight ? "border-emerald-500/40 bg-emerald-500/5 shadow-xl shadow-emerald-500/10"
                           : "border-zinc-900 bg-zinc-950/40"
              }`}>
              {p.highlight && (
                <div className="absolute -top-3 left-6 h-6 px-2.5 rounded-full bg-emerald-500 text-black text-[10px] uppercase tracking-widest font-semibold flex items-center">Populær</div>
              )}
              <div className="font-heading text-lg font-semibold text-zinc-50 mb-1">{p.name}</div>
              <div className="text-xs text-zinc-500 mb-5">{p.subtitle}</div>
              <div className="mb-6">
                <span className="font-heading text-4xl font-semibold text-zinc-50 tabular-nums">{p.price}</span>
                {p.price !== "Kontakt" && <span className="text-sm text-zinc-500 ml-1">kr/mnd</span>}
              </div>
              <ul className="space-y-2 mb-7 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a href="#kontakt" data-testid={`plan-cta-${p.key}`}
                className={`h-10 rounded-md grid place-items-center text-sm font-medium transition-all ${
                  p.highlight ? "bg-emerald-500 hover:bg-emerald-400 text-black"
                             : "border border-zinc-800 hover:border-zinc-700 text-zinc-100"
                }`}>
                {p.cta}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
function ContactForm() {
  const [form, setForm] = useState({
    name: "", company: "", orgnr: "", email: "", phone: "", message: "", plan_interest: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const resetTimer = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/public/contact`, form);
      setSent(true);
      toast.success("Takk! Vi tar kontakt innen 24 timer.");
      resetTimer.current = setTimeout(() => {
        setForm({ name: "", company: "", orgnr: "", email: "", phone: "", message: "", plan_interest: "" });
        setSent(false);
      }, 6000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke sende. Prøv igjen eller send e-post direkte.");
    } finally { setBusy(false); }
  };

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const inputCls = "w-full h-11 px-3.5 rounded-md bg-zinc-950 border border-zinc-800 text-sm focus:outline-none focus:border-emerald-500/50 focus:bg-zinc-900 transition-colors placeholder:text-zinc-600";

  return (
    <section id="kontakt" className="py-24 md:py-32 px-6 lg:px-10 border-t border-zinc-900 scroll-mt-16" data-testid="contact-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-medium mb-3">Kontakt</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-5">
            Klar til å ta neste steg?
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-8">
            Send oss noen linjer om selskapet ditt så tar vi kontakt innen 24 timer med
            forslag til demo-tidspunkt. Vi svarer alltid — helgene også.
          </p>

          <div className="space-y-3 mb-10">
            <ContactLine icon={Mail} label="E-post" value="hei@krafthub.no" href="mailto:hei@krafthub.no" />
            <ContactLine icon={Phone} label="Telefon" value="+47 400 00 000" href="tel:+4740000000" />
            <ContactLine icon={Clock} label="Svartid" value="Innen 24 timer, alle ukedager" />
            <ContactLine icon={Users} label="Dedikert" value="Snakk direkte med grunnleggeren" />
          </div>

          <div className="border border-emerald-500/20 rounded-md p-4 bg-emerald-500/5">
            <div className="flex items-center gap-2 text-emerald-400 text-xs font-medium mb-1">
              <Sparkles className="h-3.5 w-3.5" /> Tips
            </div>
            <div className="text-sm text-zinc-300 leading-relaxed">
              Kortest vei til en demo: nevn omtrent hvor mange kunder dere har og hvilket system dere bruker i dag. Da forbereder vi noe skreddersydd.
            </div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form"
          className="border border-zinc-900 rounded-lg p-6 md:p-8 bg-zinc-950/60 h-max">
          {sent ? (
            <div className="text-center py-12" data-testid="contact-success">
              <div className="h-16 w-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mx-auto mb-5">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-zinc-50 mb-2">Takk!</h3>
              <p className="text-sm text-zinc-400">Vi tar kontakt innen 24 timer på {form.email}.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <Field label="Ditt navn" required>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ola Nordmann" className={inputCls} data-testid="contact-name" />
                </Field>
                <Field label="Selskap" required>
                  <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Ditt kraftselskap AS" className={inputCls} data-testid="contact-company" />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <Field label="Organisasjonsnummer">
                  <input value={form.orgnr} onChange={(e) => setForm({ ...form, orgnr: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                    placeholder="9 sifre" className={`${inputCls} font-mono`} data-testid="contact-orgnr" />
                </Field>
                <Field label="Telefon">
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+47 …" className={inputCls} data-testid="contact-phone" />
                </Field>
              </div>
              <div className="mb-3">
                <Field label="E-post" required>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="deg@selskap.no" className={inputCls} data-testid="contact-email" />
                </Field>
              </div>
              <div className="mb-3">
                <Field label="Interessert i plan">
                  <select value={form.plan_interest} onChange={(e) => setForm({ ...form, plan_interest: e.target.value })}
                    data-testid="contact-plan"
                    className={`${inputCls} appearance-none`}>
                    <option value="">Ikke sikker ennå</option>
                    <option value="beta">Beta — opptil 500 kunder</option>
                    <option value="pro">Pro — opptil 5 000 kunder</option>
                    <option value="enterprise">Enterprise — 5 000+ kunder</option>
                  </select>
                </Field>
              </div>
              <div className="mb-5">
                <Field label="Melding">
                  <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Fortell kort om selskapet — antall kunder, dagens system, hva som er viktigst for dere."
                    className={`${inputCls} h-auto py-2.5`}
                    data-testid="contact-message" />
                </Field>
              </div>
              <button type="submit" disabled={busy} data-testid="contact-submit"
                className="w-full h-12 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 transition-all">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Send henvendelse <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-[11px] text-zinc-500 mt-3 text-center">
                Vi lagrer kun det du selv skriver inn. Ingen tredjeparter, ingen nyhetsbrev.
              </p>
            </>
          )}
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1.5 font-medium">
        {label} {required && <span className="text-emerald-400/70">*</span>}
      </label>
      {children}
    </div>
  );
}

function ContactLine({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-md bg-zinc-950 border border-zinc-900 grid place-items-center shrink-0">
        <Icon className="h-4 w-4 text-emerald-400/80" strokeWidth={1.5} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="text-sm text-zinc-200">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:text-white">{inner}</a> : inner;
}

// ============================================================
function Footer() {
  return (
    <footer className="border-t border-zinc-900 py-10 px-6 lg:px-10" data-testid="footer">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center">
            <Zap className="h-3.5 w-3.5 text-black" fill="currentColor" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-heading font-semibold text-zinc-100 text-sm">Krafthub</div>
            <div className="text-[10px] text-zinc-600">Norsk CIS/ERP for kraftleverandører</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500">
          <a href="#funksjoner" className="hover:text-zinc-300">Funksjoner</a>
          <a href="#priser" className="hover:text-zinc-300">Priser</a>
          <a href="#kontakt" className="hover:text-zinc-300">Kontakt</a>
          <Link to="/login" className="hover:text-zinc-300">Logg inn</Link>
          <span className="text-zinc-700">·</span>
          <span>© {new Date().getFullYear()} Krafthub</span>
        </div>
      </div>
    </footer>
  );
}
